import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  LineChart, Line, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar,
} from "recharts";
import api from "@/lib/api";

const RISK_BAND = (score) => {
  if (score < 1) return { label: "Sem dados", color: "#94a3b8" };
  if (score < 2) return { label: "Baixo", color: "#10b981" };
  if (score < 3) return { label: "Moderado", color: "#f59e0b" };
  if (score < 4) return { label: "Alto", color: "#f97316" };
  return { label: "Crítico", color: "#dc2626" };
};

const TAB_DEFS = [
  { id: "overview",  label: "Visão geral",     icon: "fa-chart-pie" },
  { id: "trend",     label: "Tendência",       icon: "fa-arrow-trend-up" },
  { id: "sectors",   label: "Por setor",       icon: "fa-sitemap" },
  { id: "radar",     label: "Comparativo radar", icon: "fa-bullseye" },
  { id: "table",     label: "Tabela detalhada", icon: "fa-table" },
];

const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("pt-BR"); } catch { return iso; }
};

const MultiSelect = ({ label, options, value, onChange, idKey = "id", labelKey = "label", testid }) => (
  <div data-testid={testid}>
    <label className="block text-xs font-bold uppercase tracking-wider text-slate-500 mb-1.5">{label}</label>
    <div className="border border-slate-300 rounded-lg bg-white max-h-44 overflow-y-auto p-1.5 text-sm">
      {options.length === 0 ? <div className="text-xs text-slate-400 p-2">— sem opções —</div>
        : options.map((opt) => {
          const id = typeof opt === "string" ? opt : opt[idKey];
          const text = typeof opt === "string" ? opt : (opt[labelKey] || opt.name || opt.razao_social || opt.id);
          const checked = value.includes(id);
          return (
            <label key={id} className="flex items-center gap-2 px-2 py-1 rounded hover:bg-slate-50 cursor-pointer">
              <input type="checkbox" checked={checked} className="h-3.5 w-3.5"
                     onChange={() => onChange(checked ? value.filter(v => v !== id) : [...value, id])} />
              <span className="text-slate-700 text-xs">{text}</span>
            </label>
          );
        })}
    </div>
  </div>
);

export default function DashboardAdvanced() {
  const [filters, setFilters] = useState({ companies: [], test_types: [], sectors: [] });
  const [selected, setSelected] = useState({ companies: [], test_types: [], sectors: [], date_from: "", date_to: "" });
  const [groupBy, setGroupBy] = useState("by_instrument");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("overview");
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    api.get("/dashboard/advanced/filters")
      .then((r) => setFilters(r.data))
      .catch((e) => toast.error(e.response?.data?.detail || "Erro ao carregar filtros"));
  }, []);

  const load = useMemo(() => async () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (selected.companies.length) params.set("company_ids", selected.companies.join(","));
    if (selected.test_types.length) params.set("test_types", selected.test_types.join(","));
    if (selected.sectors.length) params.set("sectors", selected.sectors.join(","));
    if (selected.date_from) params.set("date_from", selected.date_from);
    if (selected.date_to)   params.set("date_to", selected.date_to);
    params.set("group_by", groupBy);
    try {
      const r = await api.get(`/dashboard/advanced?${params.toString()}`);
      setData(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao carregar dashboard");
    } finally {
      setLoading(false);
    }
  }, [selected, groupBy]);

  useEffect(() => { load(); }, [load]);

  const exportFile = async (fmt) => {
    setExporting(true);
    const params = new URLSearchParams();
    if (selected.companies.length) params.set("company_ids", selected.companies.join(","));
    if (selected.test_types.length) params.set("test_types", selected.test_types.join(","));
    if (selected.sectors.length) params.set("sectors", selected.sectors.join(","));
    if (selected.date_from) params.set("date_from", selected.date_from);
    if (selected.date_to)   params.set("date_to", selected.date_to);
    params.set("group_by", groupBy);
    try {
      const url = `/dashboard/advanced/export.${fmt}?${params.toString()}`;
      const r = await api.get(url, { responseType: "blob" });
      const blob = new Blob([r.data]);
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `dashboard_avancado.${fmt}`;
      document.body.appendChild(link); link.click(); link.remove();
      toast.success(`Arquivo .${fmt} gerado!`);
    } catch (e) {
      toast.error(e.response?.data?.detail || `Erro ao exportar .${fmt}`);
    } finally { setExporting(false); }
  };

  const sameInstrument = useMemo(() => {
    // Detect whether the current grouping yields only one instrument (so radar
    // comparison is statistically valid)
    if (!data) return null;
    const tts = new Set(data.groups.map(g => g.test_type));
    return tts.size === 1 ? [...tts][0] : null;
  }, [data]);

  return (
    <div className="space-y-6" data-testid="dashboard-advanced">
      <header>
        <div className="flex items-center gap-3">
          <span className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-br from-indigo-500 to-indigo-700 text-white shadow-lg">
            <i className="fa-solid fa-chart-line text-xl"></i>
          </span>
          <div>
            <h1 className="text-3xl font-bold text-slate-900">Dashboard Avançado</h1>
            <p className="text-sm text-slate-500">Filtros cruzados, comparativos e tendência temporal entre avaliações.</p>
          </div>
        </div>
      </header>

      {/* Filters */}
      <section className="bg-white rounded-2xl shadow-sm border border-slate-200 p-5">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <MultiSelect label="Empresas" options={filters.companies} value={selected.companies}
                       onChange={(v) => setSelected({ ...selected, companies: v })}
                       labelKey="razao_social" testid="filter-companies" />
          <MultiSelect label="Instrumentos" options={filters.test_types} value={selected.test_types}
                       onChange={(v) => setSelected({ ...selected, test_types: v })}
                       idKey="id" labelKey="name" testid="filter-test-types" />
          <MultiSelect label="Setores / GHE" options={filters.sectors.map((s) => ({ id: s, label: s }))}
                       value={selected.sectors}
                       onChange={(v) => setSelected({ ...selected, sectors: v })}
                       testid="filter-sectors" />
          <div className="space-y-2">
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-slate-500 mb-1.5">Data inicial</label>
              <input type="date" value={selected.date_from}
                     onChange={(e) => setSelected({ ...selected, date_from: e.target.value })}
                     className="w-full border border-slate-300 rounded-lg px-3 py-1.5 text-sm" data-testid="filter-date-from" />
            </div>
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-slate-500 mb-1.5">Data final</label>
              <input type="date" value={selected.date_to}
                     onChange={(e) => setSelected({ ...selected, date_to: e.target.value })}
                     className="w-full border border-slate-300 rounded-lg px-3 py-1.5 text-sm" data-testid="filter-date-to" />
            </div>
          </div>
        </div>
        <div className="mt-4 flex flex-wrap items-center justify-between gap-3 pt-3 border-t border-slate-100">
          <div className="flex items-center gap-2 text-sm">
            <span className="text-xs font-bold uppercase tracking-wider text-slate-500">Agrupar por:</span>
            <div className="flex gap-1 bg-slate-100 rounded-lg p-1">
              {[
                { id: "by_instrument", label: "Instrumento" },
                { id: "by_company",    label: "Empresa" },
                { id: "by_sector",     label: "Setor" },
                { id: "individual",    label: "Individual" },
              ].map((g) => (
                <button key={g.id} onClick={() => setGroupBy(g.id)}
                        data-testid={`group-${g.id}`}
                        className={`px-3 py-1 rounded-md text-xs font-semibold ${groupBy === g.id ? "bg-white shadow-sm text-indigo-700" : "text-slate-600 hover:text-slate-900"}`}>
                  {g.label}
                </button>
              ))}
            </div>
          </div>
          <div className="flex gap-2">
            <button onClick={() => setSelected({ companies: [], test_types: [], sectors: [], date_from: "", date_to: "" })}
                    className="text-sm text-slate-600 hover:text-slate-900 px-3 py-1.5 rounded-lg border border-slate-300 hover:bg-slate-50"
                    data-testid="btn-clear-filters">
              <i className="fa-solid fa-rotate-left mr-1"></i> Limpar
            </button>
            <button onClick={() => exportFile("csv")} disabled={exporting || !data?.assessments?.length}
                    className="text-sm font-semibold px-3 py-1.5 rounded-lg border border-emerald-300 text-emerald-700 hover:bg-emerald-50 disabled:opacity-40" data-testid="btn-export-csv">
              <i className="fa-solid fa-file-csv mr-1"></i> CSV
            </button>
            <button onClick={() => exportFile("xlsx")} disabled={exporting || !data?.assessments?.length}
                    className="text-sm font-semibold px-3 py-1.5 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-40" data-testid="btn-export-xlsx">
              <i className="fa-solid fa-file-excel mr-1"></i> Excel
            </button>
          </div>
        </div>
      </section>

      {/* KPIs */}
      {data && (
        <section className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <KpiCard label="Avaliações" value={data.kpis.assessment_count} icon="fa-clipboard-list" tone="slate" />
          <KpiCard label="Respostas totais" value={data.kpis.total_responses.toLocaleString("pt-BR")} icon="fa-users" tone="indigo" />
          <KpiCard label="Score médio ponderado" value={data.kpis.weighted_overall_score.toFixed(2)}
                   icon="fa-gauge-high"
                   sub={<span className="text-xs font-bold px-1.5 py-0.5 rounded" style={{ background: RISK_BAND(data.kpis.weighted_overall_score).color, color: "#fff" }}>{RISK_BAND(data.kpis.weighted_overall_score).label}</span>} />
          <KpiCard label="Instrumentos usados" value={data.kpis.instruments_used} icon="fa-layer-group" tone="emerald" />
        </section>
      )}

      {/* Tabs */}
      <section className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
        <nav className="flex flex-wrap border-b border-slate-100 px-3 pt-2">
          {TAB_DEFS.map((t) => (
            <button key={t.id} onClick={() => setTab(t.id)} data-testid={`tab-${t.id}`}
                    className={`px-4 py-2 text-sm font-semibold border-b-2 transition ${tab === t.id ? "border-indigo-600 text-indigo-700" : "border-transparent text-slate-500 hover:text-slate-800"}`}>
              <i className={`fa-solid ${t.icon} mr-1.5`}></i>{t.label}
            </button>
          ))}
        </nav>
        <div className="p-6">
          {loading ? <div className="text-slate-400 py-8 text-center">Calculando…</div>
           : !data || data.kpis.assessment_count === 0 ?
            <div className="text-slate-400 py-12 text-center"><i className="fa-solid fa-filter text-3xl block mb-3"></i>Nenhuma avaliação encontrada com os filtros atuais.</div>
          : (
            <>
              {tab === "overview" && <OverviewTab data={data} />}
              {tab === "trend" && <TrendTab data={data} />}
              {tab === "sectors" && <SectorsTab selected={selected} data={data} />}
              {tab === "radar" && <RadarTab data={data} sameInstrument={sameInstrument} />}
              {tab === "table" && <TableTab data={data} />}
            </>
          )}
        </div>
      </section>
    </div>
  );
}

const KpiCard = ({ label, value, icon, tone = "slate", sub = null }) => {
  const toneCls = { indigo: "from-indigo-500 to-indigo-700",
                    emerald: "from-emerald-500 to-emerald-700",
                    slate: "from-slate-500 to-slate-700",
                    rose: "from-rose-500 to-rose-700" }[tone] || "from-slate-500 to-slate-700";
  return (
    <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-xs font-bold uppercase tracking-wider text-slate-500">{label}</div>
          <div className="text-2xl font-bold text-slate-900 mt-1">{value}</div>
          {sub && <div className="mt-1">{sub}</div>}
        </div>
        <span className={`inline-flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br ${toneCls} text-white`}>
          <i className={`fa-solid ${icon}`}></i>
        </span>
      </div>
    </div>
  );
};

const OverviewTab = ({ data }) => {
  const chartData = data.groups.map(g => ({
    name: g.label.length > 28 ? g.label.slice(0, 26) + "…" : g.label,
    score: g.overall_score,
    fill: RISK_BAND(g.overall_score).color,
  }));
  return (
    <div className="space-y-5">
      <h3 className="font-bold text-slate-800"><i className="fa-solid fa-chart-bar mr-2 text-slate-400"></i>Score global por grupo ({data.groups.length})</h3>
      <ResponsiveContainer width="100%" height={Math.max(220, chartData.length * 38)}>
        <BarChart data={chartData} layout="vertical" margin={{ top: 5, right: 20, left: 20, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
          <XAxis type="number" domain={[0, 5]} tick={{ fontSize: 11 }} />
          <YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} width={200} />
          <Tooltip contentStyle={{ fontSize: 12 }} formatter={(v) => v.toFixed(2)} />
          <Bar dataKey="score" radius={[0, 6, 6, 0]} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

const TrendTab = ({ data }) => {
  const series = data.series.map(s => ({
    date: s.date, label: s.titulo, score: s.overall_score, company: s.company_name,
  }));
  if (series.length === 0) return <div className="text-slate-400 text-center py-8">Sem séries para esta seleção.</div>;
  return (
    <div className="space-y-3">
      <h3 className="font-bold text-slate-800"><i className="fa-solid fa-arrow-trend-up mr-2 text-slate-400"></i>Tendência temporal · {series.length} pontos</h3>
      <ResponsiveContainer width="100%" height={320}>
        <LineChart data={series}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
          <XAxis dataKey="date" tick={{ fontSize: 11 }} />
          <YAxis domain={[0, 5]} tick={{ fontSize: 11 }} />
          <Tooltip contentStyle={{ fontSize: 12 }} formatter={(v) => v.toFixed(2)}
                   labelFormatter={(d, p) => p?.[0]?.payload?.label ? `${d} · ${p[0].payload.label}` : d} />
          <Line type="monotone" dataKey="score" stroke="#4338ca" strokeWidth={2.5}
                dot={{ r: 5, fill: "#4338ca" }} activeDot={{ r: 7 }} />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};

const SectorsTab = ({ data }) => {
  // Group assessments by ghe_setor
  const map = {};
  data.assessments.forEach(a => {
    const k = a.ghe_setor || "Não informado";
    if (!map[k]) map[k] = { name: k, scores: [], total_responses: 0 };
    if (a.response_count > 0) map[k].scores.push(a.overall_score);
    map[k].total_responses += a.response_count;
  });
  const rows = Object.values(map).map(s => ({
    name: s.name,
    avg: s.scores.length ? s.scores.reduce((a,b)=>a+b,0) / s.scores.length : 0,
    n: s.total_responses,
    count: s.scores.length,
  })).sort((a, b) => b.avg - a.avg);
  return (
    <div className="space-y-3">
      <h3 className="font-bold text-slate-800"><i className="fa-solid fa-sitemap mr-2 text-slate-400"></i>Comparativo por setor / GHE</h3>
      <table className="w-full text-sm" data-testid="sectors-table">
        <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
          <tr>
            <th className="px-4 py-2 text-left">Setor / GHE</th>
            <th className="px-4 py-2 text-center">Avaliações</th>
            <th className="px-4 py-2 text-center">Respostas</th>
            <th className="px-4 py-2 text-center">Score médio</th>
            <th className="px-4 py-2 text-left">Nível</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {rows.map((r) => {
            const band = RISK_BAND(r.avg);
            return (
              <tr key={r.name}>
                <td className="px-4 py-2 font-semibold text-slate-800">{r.name}</td>
                <td className="px-4 py-2 text-center text-slate-700">{r.count}</td>
                <td className="px-4 py-2 text-center text-slate-700">{r.n}</td>
                <td className="px-4 py-2 text-center font-mono font-bold">{r.avg.toFixed(2)}</td>
                <td className="px-4 py-2"><span className="inline-block px-2 py-0.5 rounded text-xs font-bold text-white" style={{ background: band.color }}>{band.label}</span></td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

const RadarTab = ({ data, sameInstrument }) => {
  if (!sameInstrument || data.groups.length < 2) {
    return (
      <div className="text-slate-500 text-center py-10">
        <i className="fa-solid fa-circle-info text-3xl block mb-3 text-slate-300"></i>
        <p className="text-sm max-w-md mx-auto">
          O comparativo radar requer pelo menos <b>2 grupos</b> do <b>mesmo instrumento</b>. Filtre por um único instrumento e use os agrupamentos &quot;Por empresa&quot; ou &quot;Por setor&quot;.
        </p>
      </div>
    );
  }
  // Build radar data: rows = dimensions, columns = group keys
  const dimNames = Array.from(new Set(data.groups.flatMap(g => g.dimensions.map(d => d.name))));
  const rows = dimNames.map(d => {
    const row = { dim: d };
    data.groups.slice(0, 4).forEach(g => {
      row[g.label.slice(0, 28)] = (g.dimensions.find(x => x.name === d)?.score) || 0;
    });
    return row;
  });
  const colors = ["#4338ca", "#0891b2", "#10b981", "#f97316"];
  return (
    <div className="space-y-3">
      <h3 className="font-bold text-slate-800"><i className="fa-solid fa-bullseye mr-2 text-slate-400"></i>Comparativo radar entre grupos · {data.groups[0].test_name}</h3>
      <ResponsiveContainer width="100%" height={380}>
        <RadarChart data={rows}>
          <PolarGrid stroke="#e2e8f0" />
          <PolarAngleAxis dataKey="dim" tick={{ fontSize: 11, fill: "#475569" }} />
          <PolarRadiusAxis angle={30} domain={[0, 5]} tick={{ fontSize: 10 }} />
          {data.groups.slice(0, 4).map((g, i) => (
            <Radar key={g.key} name={g.label.slice(0, 28)} dataKey={g.label.slice(0, 28)}
                   stroke={colors[i]} fill={colors[i]} fillOpacity={0.18} strokeWidth={2} />
          ))}
          <Legend wrapperStyle={{ fontSize: 11 }} />
          <Tooltip contentStyle={{ fontSize: 12 }} formatter={(v) => v.toFixed(2)} />
        </RadarChart>
      </ResponsiveContainer>
    </div>
  );
};

const TableTab = ({ data }) => (
  <div className="overflow-x-auto">
    <table className="w-full text-sm" data-testid="advanced-table">
      <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
        <tr>
          <th className="px-3 py-2 text-left">Empresa</th>
          <th className="px-3 py-2 text-left">Avaliação</th>
          <th className="px-3 py-2 text-left">Instrumento</th>
          <th className="px-3 py-2 text-left">Setor</th>
          <th className="px-3 py-2 text-center">Data</th>
          <th className="px-3 py-2 text-center">Resp.</th>
          <th className="px-3 py-2 text-center">Score</th>
          <th className="px-3 py-2 text-left">Nível</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-slate-100">
        {data.assessments.map(a => {
          const band = RISK_BAND(a.overall_score);
          return (
            <tr key={a.id} className="hover:bg-slate-50">
              <td className="px-3 py-2 font-medium text-slate-800">{a.company_name}</td>
              <td className="px-3 py-2"><a href={`/app/avaliacoes/${a.id}`} className="text-indigo-700 hover:underline">{a.titulo}</a></td>
              <td className="px-3 py-2 text-slate-600 text-xs">{a.test_short}</td>
              <td className="px-3 py-2 text-slate-600 text-xs">{a.ghe_setor}</td>
              <td className="px-3 py-2 text-center text-xs text-slate-500">{fmtDate(a.created_at)}</td>
              <td className="px-3 py-2 text-center">{a.response_count}</td>
              <td className="px-3 py-2 text-center font-mono font-bold">{a.overall_score.toFixed(2)}</td>
              <td className="px-3 py-2"><span className="inline-block px-2 py-0.5 rounded text-xs font-bold text-white" style={{ background: band.color }}>{band.label}</span></td>
            </tr>
          );
        })}
      </tbody>
    </table>
  </div>
);
